import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../auth/AuthProvider";
import { normalizeClaims } from "../auth/landing";

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDateTime(value) {
  const dt = toDate(value);
  if (!dt) return "—";
  return new Intl.DateTimeFormat("en-ZA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt);
}

function statusTone(status) {
  const key = String(status || "").toLowerCase();
  if (key === "open") return "bg-blue-500/20 text-blue-200 border-blue-400/20";
  if (key === "in progress") return "bg-cyan-500/20 text-cyan-200 border-cyan-400/20";
  if (key === "waiting on client" || key === "waiting") return "bg-amber-500/20 text-amber-200 border-amber-400/20";
  if (key === "resolved") return "bg-emerald-500/20 text-emerald-200 border-emerald-400/20";
  if (key === "closed") return "bg-slate-500/20 text-slate-200 border-slate-400/20";
  return "bg-white/10 text-white/70 border-white/10";
}

function priorityTone(priority) {
  const key = String(priority || "").toLowerCase();
  if (key === "critical") return "bg-rose-500/20 text-rose-200 border-rose-400/20";
  if (key === "high") return "bg-orange-500/20 text-orange-200 border-orange-400/20";
  if (key === "normal") return "bg-indigo-500/20 text-indigo-200 border-indigo-400/20";
  if (key === "low") return "bg-emerald-500/20 text-emerald-200 border-emerald-400/20";
  return "bg-white/10 text-white/70 border-white/10";
}

function Pill({ children, className = "" }) {
  return (
    <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${className}`}>
      {children}
    </span>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`rounded-2xl border border-white/10 p-4 ${tone}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-white/70">{label}</div>
      <div className="mt-2 text-4xl font-bold text-white">{value}</div>
    </div>
  );
}

function starsView(value) {
  const n = Math.max(0, Math.min(5, Number(value || 0)));
  return "★".repeat(Math.floor(n)) + "☆".repeat(5 - Math.floor(n));
}

const AREA_TOPIC_OPTIONS = {
  General: ["Permissions/settings access issue", "Filters/search issue", "General bug", "How to", "Other"],
  Overview: ["Overview card issue", "Wrong counts", "Navigation issue", "Other"],
  "Staff Dashboard": ["Widget issue", "Section issue", "Data issue", "Other"],
  Reports: ["Export issue", "Report data mismatch", "Filter issue", "Other"],
  Managers: ["Cannot add manager", "Wrong permissions", "Manager access issue", "Other"],
  Staff: ["Staff list issue", "Archive/restore issue", "Staff access issue", "Other"],
  "Active Staff": ["Clocked-in list issue", "Wrong staff shown", "Other"],
  Labor: ["Shift edit issue", "Hours mismatch", "Auto clock-out issue", "Other"],
  Payroll: ["Payroll mismatch", "Overtime issue", "Export issue", "Other"],
  Settings: ["Cannot save settings", "Permission issue", "Store setup issue", "Other"],
  Branding: ["Branding save issue", "Logo issue", "Theme issue", "Other"],
  "Activity Log": ["Activity missing", "Wrong activity", "Other"],
};


function isNeoTicket(ticket = {}) {
  const source = String(ticket.sourceApp || ticket.app || ticket.platform || "").toLowerCase();
  const number = String(ticket.ticketNumber || ticket.ref || "").toLowerCase();
  if (source) return ["neo_admin", "neo", "cashup", "cashup_admin", "neo_operations"].includes(source);
  return number.startsWith("neo-");
}

function buildTicketNumber() {
  const d = new Date();
  const y = String(d.getFullYear()).slice(-2);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `NEO-${y}${m}${day}-${rand}`;
}

export default function MyTicketsTab() {
  const { user, claims } = useAuth();
  const norm = normalizeClaims(claims || {});
  const orgId = norm.orgId || null;

  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createForm, setCreateForm] = useState({ area: "General", topic: "", priority: "Normal", description: "" });

  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, "orgs", orgId, "supportTickets"));
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter(isNeoTicket)
        .sort((a, b) => {
          const ad = toDate(a.updatedAt || a.createdAt)?.getTime() || 0;
          const bd = toDate(b.updatedAt || b.createdAt)?.getTime() || 0;
          return bd - ad;
        });
      setTickets(rows);
      setSelectedId((prev) => (prev && rows.some((x) => x.id === prev) ? prev : rows[0]?.id || ""));
    });
    return () => unsub();
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !selectedId) {
      setMessages([]);
      return;
    }
    const q = query(
      collection(db, "orgs", orgId, "supportTickets", selectedId, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
      () => setMessages([])
    );
    return () => unsub();
  }, [orgId, selectedId]);

  const filteredTickets = useMemo(() => {
    if (statusFilter === "all") return tickets;
    return tickets.filter((x) => String(x.status || "").toLowerCase() === statusFilter);
  }, [tickets, statusFilter]);

  const selectedTicket = useMemo(() => tickets.find((x) => x.id === selectedId) || null, [tickets, selectedId]);
  const selectedAreaTopics = useMemo(() => AREA_TOPIC_OPTIONS[createForm.area] || ["Other"], [createForm.area]);

  const hasSupportReply = useMemo(
    () => messages.some((message) => {
      const senderType = String(message.senderType || message.authorType || "").toLowerCase();
      const senderName = String(message.senderName || "").toLowerCase();
      return senderType === "support" || senderName.includes("invoice mate");
    }),
    [messages]
  );

  const canReply = Boolean(selectedTicket) && hasSupportReply;
  const replyBlockedMessage = "Reply opens only after Invoice Mate has replied, to prevent abuse of the chat box.";

  useEffect(() => {
    async function markClientViewed() {
      if (!orgId || !selectedTicket) return;
      const lastReplyBy = String(selectedTicket.lastReplyBy || "").toLowerCase();
      if (lastReplyBy !== "support") return;
      try {
        await updateDoc(doc(db, "orgs", orgId, "supportTickets", selectedTicket.id), {
          clientLastViewedAt: serverTimestamp(),
        });
      } catch {}
    }
    markClientViewed();
  }, [orgId, selectedTicket?.id, selectedTicket?.lastReplyBy]);

  const counts = useMemo(() => {
    const acc = { open: 0, inProgress: 0, waiting: 0, resolved: 0 };
    tickets.forEach((ticket) => {
      const key = String(ticket.status || "").toLowerCase();
      if (key === "open") acc.open += 1;
      else if (key === "in progress") acc.inProgress += 1;
      else if (key === "waiting on client" || key === "waiting") acc.waiting += 1;
      else if (key === "resolved") acc.resolved += 1;
    });
    return acc;
  }, [tickets]);

  async function handleCreateTicket() {
    if (!orgId || !user || createBusy) return;
    const topic = String(createForm.topic || "").trim();
    const description = String(createForm.description || "").trim();
    if (!topic || !description) {
      setError("Please complete the topic and description.");
      return;
    }
    setCreateBusy(true);
    setError("");
    try {
      const ticketNumber = buildTicketNumber();
      const ticketRef = await addDoc(collection(db, "orgs", orgId, "supportTickets"), {
        ticketNumber,
        orgId,
        orgName: norm.orgName || norm.organizationName || norm.org || "",
        area: createForm.area || "General",
        topic,
        priority: createForm.priority || "Normal",
        description,
        createdByUid: user.uid || "",
        createdByName: user.displayName || user.email || "Client",
        createdByEmail: user.email || "",
        sourceApp: "neo_admin",
        status: "open",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastReplyAt: serverTimestamp(),
        lastReplyBy: "client",
      });

      await addDoc(collection(db, "orgs", orgId, "supportTickets", ticketRef.id, "messages"), {
        body: description,
        senderType: "client",
        senderUid: user?.uid || "",
        senderName: user?.displayName || user?.email || "Client",
        createdAt: serverTimestamp(),
      });

      setCreateOpen(false);
      setCreateForm({ area: "General", topic: "", priority: "Normal", description: "" });
      setSelectedId(ticketRef.id);
    } catch (err) {
      console.error(err);
      setError("Could not create your ticket right now.");
    } finally {
      setCreateBusy(false);
    }
  }

  async function handleSendReply() {
    if (!orgId || !selectedTicket || !reply.trim() || !canReply || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await addDoc(collection(db, "orgs", orgId, "supportTickets", selectedTicket.id, "messages"), {
        body: reply.trim(),
        senderType: "client",
        senderUid: user?.uid || "",
        senderName: user?.displayName || user?.email || "Client",
        createdAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "orgs", orgId, "supportTickets", selectedTicket.id), {
        status: "open",
        updatedAt: serverTimestamp(),
        lastReplyAt: serverTimestamp(),
        lastReplyBy: "client",
      });

      setReply("");
    } catch (err) {
      console.error(err);
      setError("Could not send your reply right now.");
    } finally {
      setSubmitting(false);
    }
  }

  async function markResolved() {
    if (!orgId || !selectedTicket || ratingBusy) return;
    setRatingBusy(true);
    setError("");
    try {
      await updateDoc(doc(db, "orgs", orgId, "supportTickets", selectedTicket.id), {
        status: "resolved",
        resolvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      setError("Could not update ticket status.");
    } finally {
      setRatingBusy(false);
    }
  }

  return (
    <div className="space-y-6" style={{ color: "var(--brand-text)" }}>
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">My Tickets</h1>
          <p className="mt-2 text-sm opacity-75">Track your support requests, references, replies, and ratings.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setCreateOpen(true); setError(""); }}
            className="rounded-2xl bg-cyan-500 px-4 py-2 font-semibold text-slate-950 hover:bg-cyan-400"
          >
            + New Ticket
          </button>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-2xl border border-white/10 bg-transparent px-4 py-2"
          >
            <option value="all" style={{ background: "#09101a" }}>All statuses</option>
            <option value="open" style={{ background: "#09101a" }}>Open</option>
            <option value="in progress" style={{ background: "#09101a" }}>In Progress</option>
            <option value="waiting on client" style={{ background: "#09101a" }}>Waiting</option>
            <option value="resolved" style={{ background: "#09101a" }}>Resolved</option>
            <option value="closed" style={{ background: "#09101a" }}>Closed</option>
          </select>
          <div className="rounded-2xl border border-white/10 px-4 py-2 text-sm">{tickets.length} tickets</div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <StatCard label="Open" value={counts.open} tone="bg-blue-500/10" />
        <StatCard label="In Progress" value={counts.inProgress} tone="bg-cyan-500/10" />
        <StatCard label="Waiting" value={counts.waiting} tone="bg-amber-500/10" />
        <StatCard label="Resolved" value={counts.resolved} tone="bg-emerald-500/10" />
      </div>

      <div className="grid gap-4 xl:grid-cols-[320px,1fr]">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] overflow-hidden">
          <div className="border-b border-white/10 px-4 py-4 text-lg font-semibold">Ticket list</div>
          <div className="max-h-[760px] overflow-auto">
            {filteredTickets.length ? (
              filteredTickets.map((ticket) => {
                const selected = ticket.id === selectedId;
                const lastReplyBy = String(ticket.lastReplyBy || "").toLowerCase();
                const lastReplyAt = toDate(ticket.lastReplyAt);
                const viewedAt = toDate(ticket.clientLastViewedAt);
                const hasUnreadSupport = lastReplyBy === "support" && lastReplyAt && (!viewedAt || lastReplyAt > viewedAt);
                return (
                  <button
                    key={ticket.id}
                    onClick={() => setSelectedId(ticket.id)}
                    className={`w-full border-b border-white/10 px-4 py-4 text-left transition ${selected ? "bg-cyan-500/12" : "hover:bg-white/[0.04]"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-semibold">{ticket.ticketNumber || ticket.reference || ticket.id}</div>
                      <div className="flex items-center gap-2">
                        {hasUnreadSupport ? <span className="h-2.5 w-2.5 rounded-full bg-rose-400" /> : null}
                        <Pill className={statusTone(ticket.status)}>{ticket.status || "Open"}</Pill>
                      </div>
                    </div>
                    <div className="mt-2 font-medium opacity-95">{ticket.topic || ticket.subject || "Support ticket"}</div>
                    <div className="mt-1 text-sm opacity-70">
                      {ticket.area || "General"} · {ticket.priority || "Normal"}
                    </div>
                    {ticket?.rating?.stars ? <div className="mt-2 text-amber-300">{starsView(ticket.rating.stars)}</div> : null}
                    <div className="mt-2 text-xs opacity-50">Created {fmtDateTime(ticket.createdAt)}</div>
                  </button>
                );
              })
            ) : (
              <div className="p-4 text-sm opacity-60">No tickets found for this filter.</div>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-4 md:p-5">
          {selectedTicket ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">Reference</div>
                  <div className="mt-1 text-3xl font-bold">{selectedTicket.ticketNumber || selectedTicket.reference || selectedTicket.id}</div>
                  <div className="mt-2 text-xl font-medium">{selectedTicket.topic || selectedTicket.subject || "Support ticket"}</div>
                </div>
                <div className="flex gap-2">
                  <Pill className={statusTone(selectedTicket.status)}>{selectedTicket.status || "Open"}</Pill>
                  <Pill className={priorityTone(selectedTicket.priority)}>{selectedTicket.priority || "Normal"}</Pill>
                </div>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-white/10 p-4">
                  <div className="text-xs uppercase tracking-[0.18em] opacity-60">Area</div>
                  <div className="mt-2 text-2xl">{selectedTicket.area || "General"}</div>
                </div>
                <div className="rounded-2xl border border-white/10 p-4">
                  <div className="text-xs uppercase tracking-[0.18em] opacity-60">Created</div>
                  <div className="mt-2 text-2xl">{fmtDateTime(selectedTicket.createdAt)}</div>
                </div>
                <div className="rounded-2xl border border-white/10 p-4">
                  <div className="text-xs uppercase tracking-[0.18em] opacity-60">Last updated</div>
                  <div className="mt-2 text-2xl">{fmtDateTime(selectedTicket.updatedAt || selectedTicket.lastReplyAt || selectedTicket.createdAt)}</div>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-white/10 p-4">
                <div className="text-xs uppercase tracking-[0.18em] opacity-60">Original request</div>
                <div className="mt-3 whitespace-pre-wrap text-lg">{selectedTicket.description || selectedTicket.message || "—"}</div>
              </div>

              <div className="mt-5">
                <div className="text-lg font-semibold">Conversation</div>
                <div className="mt-3 space-y-3">
                  {messages.map((message) => {
                    const senderType = String(message.senderType || message.authorType || "").toLowerCase();
                    const senderName = String(message.senderName || "").trim() || "Support";
                    const isSupport = senderType === "support" || senderName.toLowerCase().includes("invoice mate");
                    return (
                      <div
                        key={message.id}
                        className={`rounded-2xl border p-4 ${isSupport ? "border-emerald-400/25 bg-emerald-500/12" : "border-blue-400/25 bg-blue-500/12"}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className={`text-xl font-semibold ${isSupport ? "text-emerald-200" : "text-blue-200"}`}>{senderName}</div>
                          <div className="text-sm opacity-60">{fmtDateTime(message.createdAt)}</div>
                        </div>
                        <div className="mt-3 whitespace-pre-wrap text-lg">{message.body || "—"}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {selectedTicket?.rating?.stars ? (
                <div className="mt-5 rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold uppercase tracking-[0.18em] text-amber-200">Your rating</div>
                    <div className="text-amber-300">{starsView(selectedTicket.rating.stars)}</div>
                  </div>
                  {selectedTicket?.rating?.comment ? <div className="mt-3 text-sm opacity-85">{selectedTicket.rating.comment}</div> : null}
                </div>
              ) : null}

              <div className="mt-5">
                {!hasSupportReply ? (
                  <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4 text-sm text-amber-100">
                    {replyBlockedMessage}
                  </div>
                ) : null}

                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  disabled={!canReply || submitting}
                  placeholder={hasSupportReply ? "Write a reply to support..." : "You can reply here once Invoice Mate has replied."}
                  className={`mt-3 w-full rounded-2xl border px-4 py-3 resize-none ${canReply ? "border-white/10 bg-transparent" : "border-white/5 bg-white/[0.02] opacity-60"}`}
                />

                {error ? <div className="mt-3 text-sm text-rose-300">{error}</div> : null}

                <div className="mt-4 flex flex-wrap justify-end gap-3">
                  <button
                    onClick={markResolved}
                    disabled={ratingBusy || !selectedTicket}
                    className="rounded-2xl bg-emerald-500/20 px-5 py-3 font-semibold text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50"
                  >
                    Mark Resolved
                  </button>
                  <button
                    onClick={handleSendReply}
                    disabled={!canReply || !reply.trim() || submitting}
                    className="rounded-2xl bg-cyan-500 px-5 py-3 font-semibold text-slate-950 hover:bg-cyan-400 disabled:opacity-50"
                  >
                    {submitting ? "Sending..." : "Send Reply"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-6 opacity-70">Select a ticket to view it.</div>
          )}
        </div>
      </div>

      {createOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-[#09101a] p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold">Create support ticket</h2>
                <p className="mt-1 text-sm opacity-70">Log a support request and track replies here.</p>
              </div>
              <button onClick={() => !createBusy && setCreateOpen(false)} className="rounded-xl border border-white/10 px-3 py-2 hover:bg-white/5">Close</button>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="block">
                <div className="mb-2 text-xs uppercase tracking-[0.18em] opacity-60">Area</div>
                <select
                  value={createForm.area}
                  onChange={(e) => setCreateForm((p) => ({ ...p, area: e.target.value, topic: "" }))}
                  className="w-full rounded-2xl border border-white/10 bg-transparent px-4 py-3"
                >
                  {Object.keys(AREA_TOPIC_OPTIONS).map((area) => (
                    <option key={area} value={area} style={{ background: "#09101a" }}>{area}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <div className="mb-2 text-xs uppercase tracking-[0.18em] opacity-60">Priority</div>
                <select
                  value={createForm.priority}
                  onChange={(e) => setCreateForm((p) => ({ ...p, priority: e.target.value }))}
                  className="w-full rounded-2xl border border-white/10 bg-transparent px-4 py-3"
                >
                  {["Low", "Normal", "High", "Critical"].map((p) => (
                    <option key={p} value={p} style={{ background: "#09101a" }}>{p}</option>
                  ))}
                </select>
              </label>

              <label className="block md:col-span-2">
                <div className="mb-2 text-xs uppercase tracking-[0.18em] opacity-60">Topic</div>
                <select
                  value={createForm.topic}
                  onChange={(e) => setCreateForm((p) => ({ ...p, topic: e.target.value }))}
                  className="w-full rounded-2xl border border-white/10 bg-transparent px-4 py-3"
                >
                  <option value="" style={{ background: "#09101a" }}>Select a topic…</option>
                  {selectedAreaTopics.map((topic) => (
                    <option key={topic} value={topic} style={{ background: "#09101a" }}>{topic}</option>
                  ))}
                </select>
              </label>

              <label className="block md:col-span-2">
                <div className="mb-2 text-xs uppercase tracking-[0.18em] opacity-60">Description</div>
                <textarea
                  rows={6}
                  value={createForm.description}
                  onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Tell support what happened, what you expected, and how to reproduce it."
                  className="w-full rounded-2xl border border-white/10 bg-transparent px-4 py-3 resize-none"
                />
              </label>
            </div>

            {error ? <div className="mt-4 text-sm text-rose-300">{error}</div> : null}

            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => !createBusy && setCreateOpen(false)} className="rounded-2xl border border-white/10 px-5 py-3 hover:bg-white/5">Cancel</button>
              <button onClick={handleCreateTicket} disabled={createBusy} className="rounded-2xl bg-cyan-500 px-5 py-3 font-semibold text-slate-950 hover:bg-cyan-400 disabled:opacity-50">
                {createBusy ? "Creating..." : "Create Ticket"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
